import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiAdmin, getDisplayMessage } from '@/api/apiClient';
import { RotateCcw, Building2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { DIAS_RETENCAO_LIXEIRA } from '@/lib/trashRetention';

const ENTITY_OPTIONS = [
  { value: 'documento', label: 'Documento' },
  { value: 'licoes_aprendida', label: 'Lições aprendidas' },
  { value: 'edital', label: 'Licitação (edital)' },
  { value: 'proposta', label: 'Proposta' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'atestado_tecnico', label: 'Atestado técnico' },
];

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

export default function AdminRecuperar() {
  const [empresas, setEmpresas] = useState([]);
  const [empresasLoading, setEmpresasLoading] = useState(true);
  const [empresaId, setEmpresaId] = useState('');
  const [entityType, setEntityType] = useState('documento');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loadingItems, setLoadingItems] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reason, setReason] = useState('');
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setEmpresasLoading(true);
      try {
        const data = await apiAdmin.get('/api/admin/empresas', { page: 1, limit: 500 });
        if (cancelled) return;
        setEmpresas(data?.empresas ?? []);
      } catch (e) {
        if (!cancelled) toast.error(getDisplayMessage(e, 'Erro ao carregar empresas'));
      } finally {
        if (!cancelled) setEmpresasLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadItems = useCallback(async () => {
    if (!empresaId) {
      setItems([]);
      setPagination({ total: 0, pages: 1 });
      return;
    }
    setLoadingItems(true);
    try {
      const data = await apiAdmin.get('/api/admin/recuperacao/items', {
        empresaId,
        entityType,
        page,
        limit: 20,
      });
      setItems(data?.items ?? []);
      setPagination({
        total: data?.total ?? 0,
        pages: data?.pages ?? 1,
      });
    } catch (e) {
      toast.error(getDisplayMessage(e, 'Erro ao listar lixeira'));
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, [empresaId, entityType, page]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openRestore = (item) => {
    setSelectedItem(item);
    setReason('');
    setRestoreOpen(true);
  };

  const confirmRestore = async () => {
    const r = reason.trim();
    if (r.length < 3) {
      toast.error('Informe um motivo com pelo menos 3 caracteres');
      return;
    }
    if (!selectedItem || !empresaId) return;
    setRestoring(true);
    try {
      const out = await apiAdmin.post('/api/admin/recuperacao/restore', {
        empresaId,
        entityType: selectedItem.entityType,
        entityId: selectedItem.id,
        reason: r,
      });
      toast.success(
        out?.alreadyActive ? 'Registro já estava ativo' : 'Registro restaurado com sucesso'
      );
      setRestoreOpen(false);
      setSelectedItem(null);
      await loadItems();
    } catch (e) {
      toast.error(getDisplayMessage(e, 'Falha ao restaurar'));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <RotateCcw className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recuperar</h1>
          <p className="text-sm text-muted-foreground">
            Itens movidos para a lixeira por usuários da empresa (retenção {DIAS_RETENCAO_LIXEIRA} dias).
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5" />
            Filtros
          </CardTitle>
          <CardDescription>
            Selecione a empresa e o tipo de registro. A listagem mostra apenas itens ainda dentro do prazo de
            recuperação.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 items-end">
          <div className="space-y-2 min-w-[240px] flex-1">
            <Label>Empresa</Label>
            {empresasLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select value={empresaId || undefined} onValueChange={(v) => { setEmpresaId(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {empresas.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nomeFantasia || e.razaoSocial || e.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-2 min-w-[200px]">
            <Label>Tipo</Label>
            <Select
              value={entityType}
              onValueChange={(v) => {
                setEntityType(v);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" variant="outline" size="icon" onClick={() => loadItems()} disabled={loadingItems}>
            <RefreshCw className={`h-4 w-4 ${loadingItems ? 'animate-spin' : ''}`} />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lixeira</CardTitle>
          <CardDescription>
            {empresaId
              ? `${pagination.total} item(ns) — página ${page} de ${pagination.pages || 1}`
              : 'Escolha uma empresa para listar os itens.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!empresaId ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Selecione uma empresa acima.</p>
          ) : loadingItems ? (
            <div className="space-y-2 py-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Nenhum item na lixeira para este filtro.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Excluído em</TableHead>
                    <TableHead>Expira em</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead className="w-[120px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium max-w-[280px] truncate" title={row.title || row.id}>
                        {row.title || row.id}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(row.deletedAt)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(row.trashExpiresAt)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.deletedByEmail || '—'}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="secondary" onClick={() => openRestore(row)}>
                          Restaurar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {pagination.pages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= pagination.pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restaurar registro</DialogTitle>
            <DialogDescription>
              O registro voltará a aparecer para a empresa. Esta ação fica registrada no log de atividades e no log do
              super admin.
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="text-sm space-y-1 py-2">
              <p>
                <span className="text-muted-foreground">Item:</span>{' '}
                {selectedItem.title || selectedItem.id}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="restore-reason">Motivo (obrigatório)</Label>
            <Textarea
              id="restore-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: Solicitação do cliente por e-mail ticket #123"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)} disabled={restoring}>
              Cancelar
            </Button>
            <Button onClick={confirmRestore} disabled={restoring}>
              {restoring ? 'Restaurando…' : 'Confirmar restauração'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
